<template>
  <div class="landing-feed-preview">
    <!-- Open Feed — real items from the public timeline -->
    <template v-if="feedMode === 'open'">
      <!-- Loading skeleton -->
      <div v-if="isLoading" class="masonry-grid">
        <div v-for="c in columnCount" :key="'skel-col-' + c" class="masonry-column">
          <div v-for="i in 3" :key="'skel-' + c + '-' + i" class="skeleton-card">
            <div class="skeleton-image shimmer" :style="{ aspectRatio: i % 2 === 0 ? '1/1' : '3/4' }"></div>
            <div class="skeleton-caption shimmer"></div>
          </div>
        </div>
      </div>

      <!-- Real masonry grid -->
      <div v-else-if="columns.length" class="masonry-grid">
        <div
          v-for="(col, ci) in columns"
          :key="'col-' + ci"
          class="masonry-column"
        >
          <figure
            v-for="item in col"
            :key="item.id"
            class="masonry-item"
          >
            <!-- Title band (same pattern as MasonryMarketplace) -->
            <div
              class="masonry-item-title-row"
              v-if="hasRealTitle(item) && hasImages(item)"
            >
              <div class="masonry-item-title">
                {{ truncateTitle(getDisplayTitle(item)) }}
              </div>
            </div>
            <div class="image-container">
              <ImageCard :item="item" @select="handleCardClick" />
            </div>
          </figure>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <p>No public items yet — be the first to post!</p>
      </div>
    </template>

    <!-- Organisation Feed — masonry-like entry points -->
    <div v-else class="masonry-grid org-masonry">
      <!-- Column 1: Intro card + placeholder -->
      <div class="masonry-column">
        <div class="org-intro-card">
          <h2 class="poster-title">This will be your organisation exclusive feed.</h2>
          <UnitButton variant="secondary" @click="openRegister" v-haptic>
            Create your organisation
          </UnitButton>
        </div>

        <div
          class="innerSticker placeholder-card ph-tall"
          :style="{
            background: 'var(--color-landing-feed-placeholder-surface-1)',
          }"
          @click="openRegister"
        >
          <div class="ph-pattern">
            <span v-for="n in 40" :key="n">Login or register</span>
          </div>
        </div>
      </div>

      <!-- Column 2: Org 1 poster + placeholder -->
      <div class="masonry-column">
        <div class="org-poster" @click="openRegister">
          <h2 class="poster-title">Org 1</h2>
          <p class="org-card-desc">Join Unit's first open organisation</p>
          <UnitButton variant="secondary" id="joinOrg1" v-haptic>
            Join Org 1
          </UnitButton>
        </div>

        <div
          class="innerSticker placeholder-card ph-short"
          :style="{
            background: 'var(--color-landing-feed-placeholder-surface-2)',
          }"
          @click="openRegister"
        >
          <div class="ph-pattern">
            <span v-for="n in 40" :key="n">Login or register</span>
          </div>
        </div>
      </div>

      <!-- Column 3: Invite + placeholders -->
      <div class="masonry-column">
        <div class="invite-intro">
          <p class="invite-intro-text">Already have an invite link? Paste it below to join an organisation directly.</p>
          <UseInviteLink />
        </div>

        <div
          class="innerSticker placeholder-card ph-medium"
          :style="{
            background: 'var(--color-landing-feed-placeholder-surface-3)',
          }"
          @click="openRegister"
        >
          <div class="ph-pattern">
            <span v-for="n in 40" :key="n">Login or register</span>
          </div>
        </div>

        <div
          class="innerSticker placeholder-card ph-short"
          :style="{
            background: 'var(--color-landing-feed-placeholder-surface-4)',
          }"
          @click="openRegister"
        >
          <div class="ph-pattern">
            <span v-for="n in 40" :key="n">Login or register</span>
          </div>
        </div>
      </div>
    </div>

    <ImageSlideshowOverlay
      :visible="overlayVisible"
      :item="overlayItem"
      :can-navigate-previous="canNavigatePrevious"
      :can-navigate-next="canNavigateNext"
      :item-navigation-loading="isLoadingMore"
      @close="closeOverlay"
      @next="navigateNext"
      @prev="navigatePrevious"
    />
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import {
  fetchTimelineItems,
  normalizeTimelineItems,
} from "@/utils/timelineFetcher";
import ImageCard from "@/components/DashboardComponents/EcosystemComponents/MasonryMarketplaceComponents/ImageCard.vue";
import UseInviteLink from "@/components/LandingComponents/UseInviteLink.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import ImageSlideshowOverlay from "@/components/GenericComponents/ImageSlideshowOverlay.vue";
import { smartTitleCase } from "@/utils/smartTitleCase";
import iconsetPath from "@/assets/icons/iconset.svg";
import {
  closeItemOverlayRoute,
  findAdjacentFeedItem,
  openItemOverlayRoute,
  replaceItemOverlayRoute,
} from "@/utils/overlayNavigation";
import { preloadAdjacentOverlayItems } from "@/utils/overlayMediaPreload";

export default {
  name: "LandingFeedPreview",
  components: { ImageCard, ImageSlideshowOverlay, UseInviteLink, UnitButton },
  props: {
    feedMode: { type: String, default: "open" }, // 'open' | 'org'
  },
  emits: ["register"],
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const isLoading = ref(true);
    const isLoadingMore = ref(false);
    const items = ref([]);
    const lastDoc = ref(null);
    const hasMore = ref(true);
    const overlayVisible = ref(false);
    const overlayItem = ref(null);

    // Auth modal helpers
    const openLogin = () => store.dispatch("toggleLoginModal");
    const openRegister = () => store.dispatch("toggleRegisterModal");

    // ─── Column logic (matches MasonryMarketplace) ───
    const getColumnCount = () => {
      const w = window.innerWidth;
      if (w < 590) return 2;
      if (w < 1080) return 3;
      return 3;
    };

    const columnCount = ref(getColumnCount());

    const distributeToColumns = (list, numCols) => {
      const cols = Array.from({ length: numCols }, () => []);
      list.forEach((item, i) => cols[i % numCols].push(item));
      return cols;
    };

    const columns = computed(() =>
      distributeToColumns(items.value, columnCount.value)
    );

    // Resize handler
    let resizeTimer = null;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        columnCount.value = getColumnCount();
      }, 150);
    };

    // ─── Title helpers (same as MasonryMarketplace) ───
    const sanitizeTitle = (raw) => {
      const t = String(raw || "").trim();
      if (!t) return "";
      const lower = t.toLowerCase();
      const placeholders = [
        "edit title", "give your post a title", "add a title", "post title",
      ];
      if (placeholders.some((p) => lower === p || lower.startsWith(p))) return "";
      return t;
    };
    const getDisplayTitle = (item) =>
      smartTitleCase(sanitizeTitle(item?.itemName || item?.title || ""));
    const hasRealTitle = (item) => !!getDisplayTitle(item);
    const truncateTitle = (t) =>
      t && t.length > 80 ? t.substring(0, 80).trimEnd() + "…" : t;
    const hasImages = (item) => {
      try {
        if (Array.isArray(item?.images) && item.images.length > 0) return true;
        if (item?.thumbnail) return true;
        if (item?.mediaUrl) return true;
        // Embed posts (YouTube/SoundCloud) have visual content but no images array
        if (item?.embedHtml || item?.mediaType === "embed") return true;
        // Audio items have visual content (the player)
        if (item?.mediaType === "audio") return true;
      } catch {}
      return false;
    };

    const handleCardClick = (item) => {
      console.log('[LandingFeedPreview] handleCardClick fired', { id: item?.id, item });
      if (!item?.id) {
        console.warn('[LandingFeedPreview] item has no id, aborting');
        return;
      }
      overlayItem.value = item;
      overlayVisible.value = true;
      store.dispatch("UIState/setLocalOverlayActive", true);
      openItemOverlayRoute(
        router,
        route,
        item.itemId || item.id,
        item._authorityPresentation ? item.organisation : null,
      );
      nextTick(prefetchNextPageIfNeeded);
    };

    const currentIndex = computed(() =>
      items.value.findIndex((item) => item?.id === overlayItem.value?.id),
    );
    const canNavigatePrevious = computed(() => currentIndex.value > 0);
    const canNavigateNext = computed(() =>
      currentIndex.value >= 0 && (
        currentIndex.value < items.value.length - 1 || hasMore.value
      ),
    );

    const setOverlayItem = (item) => {
      if (!item?.id) return;
      overlayItem.value = item;
      replaceItemOverlayRoute(
        router,
        route,
        item.itemId || item.id,
        item._authorityPresentation ? item.organisation : null,
      );
    };

    let paginationPromise = null;
    const loadNextPage = () => {
      if (paginationPromise) return paginationPromise;
      if (!hasMore.value) return Promise.resolve(false);
      paginationPromise = (async () => {
        isLoadingMore.value = true;
        try {
          const result = await fetchTimelineItems({
            batchSize: 12,
            lastDoc: lastDoc.value,
          });
          const nextItems = normalizeTimelineItems(result?.items || []);
          const knownIds = new Set(items.value.map((item) => item.id));
          items.value = [
            ...items.value,
            ...nextItems.filter((item) => item?.id && !knownIds.has(item.id)),
          ];
          lastDoc.value = result?.lastDoc || null;
          hasMore.value = Boolean(result?.hasMore && result?.lastDoc);
          return nextItems.length > 0;
        } catch (err) {
          console.warn("[LandingFeedPreview] Failed to load next page:", err);
          return false;
        } finally {
          isLoadingMore.value = false;
          paginationPromise = null;
        }
      })();
      return paginationPromise;
    };

    const prefetchNextPageIfNeeded = () => {
      if (
        overlayVisible.value &&
        currentIndex.value === items.value.length - 1 &&
        hasMore.value
      ) loadNextPage();
    };

    watch(
      [overlayVisible, () => overlayItem.value?.id, items],
      ([visible, currentId]) => {
        if (!visible || !currentId) return;
        preloadAdjacentOverlayItems({ items: items.value, currentId });
      },
      { flush: "post" },
    );

    let nextNavigationPromise = null;
    const navigateNext = () => {
      if (!overlayItem.value) return Promise.resolve(false);
      let nextItem = findAdjacentFeedItem(items.value, overlayItem.value.id, "next");
      if (nextItem) {
        setOverlayItem(nextItem);
        nextTick(prefetchNextPageIfNeeded);
        return Promise.resolve(true);
      }

      if (!hasMore.value) return Promise.resolve(false);
      if (nextNavigationPromise) return nextNavigationPromise;

      const requestedItemId = overlayItem.value.id;
      nextNavigationPromise = (async () => {
        try {
          await loadNextPage();
          if (overlayItem.value?.id !== requestedItemId) return false;
          nextItem = findAdjacentFeedItem(items.value, requestedItemId, "next");
          if (!nextItem) return false;
          setOverlayItem(nextItem);
          nextTick(prefetchNextPageIfNeeded);
          return true;
        } finally {
          nextNavigationPromise = null;
        }
      })();
      return nextNavigationPromise;
    };

    const navigatePrevious = () => {
      if (!overlayItem.value) return;
      const previousItem = findAdjacentFeedItem(
        items.value,
        overlayItem.value.id,
        "prev",
      );
      if (previousItem) setOverlayItem(previousItem);
    };

    const closeOverlay = () => {
      overlayVisible.value = false;
      overlayItem.value = null;
      store.dispatch("UIState/setLocalOverlayActive", false);
      closeItemOverlayRoute(router, route);
    };

    // ─── Fetch the last 12 public items ───
    const loadPublicFeed = async () => {
      isLoading.value = true;
      try {
        const result = await fetchTimelineItems({ batchSize: 12 });
        if (result?.items?.length) {
          items.value = normalizeTimelineItems(result.items);
        }
        lastDoc.value = result?.lastDoc || null;
        hasMore.value = Boolean(result?.hasMore && result?.lastDoc);
      } catch (err) {
        console.warn("[LandingFeedPreview] Failed to load feed:", err);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener("resize", onResize);
      loadPublicFeed();
    });

    onUnmounted(() => {
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
      if (overlayVisible.value) {
        store.dispatch("UIState/setLocalOverlayActive", false);
      }
    });

    return {
      isLoading,
      items,
      columns,
      columnCount,
      iconsetPath,
      hasRealTitle,
      hasImages,
      getDisplayTitle,
      truncateTitle,
      handleCardClick,
      openLogin,
      openRegister,
      overlayVisible,
      overlayItem,
      canNavigatePrevious,
      canNavigateNext,
      isLoadingMore,
      closeOverlay,
      navigateNext,
      navigatePrevious,
    };
  },
};
</script>

<style scoped>
/* ─── Layout ─── */
.landing-feed-preview {
  width: 100%;
  min-height: 80vh;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
}

/* ─── Masonry grid (matches real marketplace) ─── */
.masonry-grid {
  display: flex;
  gap: var(--space-3);
  width: 100%;
}

.masonry-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.masonry-item {
  background: var(--color-surface-canvas);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  overflow: hidden;
  margin: 0; /* reset default figure margin */
  /* Pad bottom to replicate the info-block spacing from MasonryMarketplace
     (dropdown + caption row) that doesn't render on the landing page */
  padding-bottom: var(--space-4);
}

/* Masonry item title row - matches MasonryMarketplace */
.masonry-item-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-2);
  width: 100%;
  max-width: 100%;
}

/* Masonry item title - matches MasonryMarketplace */
.masonry-item-title {
  font-size: var(--font-size-heading);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  white-space: normal;
}

.image-container {
  width: 100%;
}

/* Alpha-capable media: remove backgrounds from parent card */
.masonry-item:has(.transparent-media) {
  background: transparent;
}

/* Make cards clickable — opens overlay */
.masonry-item {
  cursor: pointer;
}

/* ─── Loading skeletons ─── */
.skeleton-card {
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-surface-canvas);
}
.skeleton-image {
  width: 100%;
}
.skeleton-caption {
  height: 12px;
  margin: var(--space-3);
  border-radius: 6px;
}
.shimmer {
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ─── Org masonry entry points ─── */
.org-masonry {
  min-height: 80vh;
}

/* Special intro card — first in org feed */
.org-intro-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  text-align: left;
  border-radius: var(--space-3);
  padding:  var(--space-7) 0 var(--space-7) 0;
  gap: var(--space-4);
}

.org-card-desc {
  margin: 0;
  font-size: var(--font-size-label);
  opacity: 0.7;
  line-height: 1.4;
}

/* Invite intro wrapper */
.invite-intro {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.invite-intro-text {
  margin: 0;
  font-size: var(--font-size-label);
  line-height: 1.4;
  text-align: left;
}

/* Org 1 poster card — like text-only masonry items */
.org-poster {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  text-align: left;
  aspect-ratio: 3/4;
  cursor: pointer;
  transition: transform 0.15s;
  background: var(--color-landing-feed-placeholder-surface-2);
  border-radius: var(--space-3);
  padding: var(--space-4);
  gap: var(--space-3);
}
.org-poster:hover {
  transform: translateY(var(--motion-pressed-offset));
}
.poster-title {
  margin-bottom: var(--space-7);
  font-size: 2.4rem;

  letter-spacing: -0.02em;
  line-height: 1.1;
  font-weight: 300;


}


#joinOrg1 {
  margin-top: var(--space-7);
}
/* ─── Placeholder image cards ─── */
.placeholder-card {
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  position: relative;
  border-radius: var(--space-3);
  transition: transform 0.15s;
}
.placeholder-card:hover {
  transform: translateY(var(--motion-pressed-offset));
}

/* Height variants for organic feel */
.ph-tall   { aspect-ratio: 3/5; }
.ph-medium { aspect-ratio: 4/5; }
.ph-short  { aspect-ratio: 1/1; }

/* 45° repeating text pattern */
.ph-pattern {
  position: absolute;
  inset: -50%;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  gap: 12px 18px;
  transform: rotate(-45deg);
  pointer-events: none;
}
.ph-pattern span {
  font-size: var(--font-size-label);
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  line-height: 2.6;
}

/* ─── Empty state ─── */
.empty-state {
  padding: var(--space-8);
  text-align: center;
  color: var(--color-text-muted);
}

/* ─── Responsive: match MasonryMarketplace title sizing ─── */
@media (max-width: 590px) {
  .masonry-item-title {
    font-size: var(--font-size-title);
  }
  .poster-title {
    font-size: 1.4rem;
  }
  /* On mobile: invite row on top, then 2-column grid for org cards */
  .org-masonry {
    display: flex;
    flex-wrap: wrap;
  }
  .org-masonry > .masonry-column:nth-child(3) {
    order: -1;
    flex: 0 0 100%;
    width: 100%;
  }
  .org-masonry > .masonry-column:nth-child(1),
  .org-masonry > .masonry-column:nth-child(2) {
    flex: 1 1 0;
    min-width: 0;
  }
}


</style>
